import React, { useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { useDispatch } from "Hooks/useDispatch";

import classes from "./CodeEditor.scss";

import {
  Button,
  ButtonDisclosure,
  Dropdown,
  DropdownMenu,
  IconButton,
  Modal,
} from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";

import Editor from "@monaco-editor/react";

import { CloseOutlined } from "@toddle-edu/ds-icons";
import Loading from "UIComponents/Loading";

import { updateSettings } from "modules/ConfigurationThunks";

import {
  themeJson,
  getInitialCodeEditorObj,
  getFormattedCode,
  MARKDOWN,
  persistLanguagePreferenceSetting,
} from "./utils.js";
import { CODE_EDITOR_PROGRAMMING_LANGUAGES } from "@toddle-edu/common-utils";

const styles = {
  modalBodyStyle: {
    padding: "0px",
  },
};

const CodeEditor = props => {
  const {
    input,
    inputFormat,
    outputFormat,
    isOpen,
    onCloseHandler,
    onAddCodeHandler,
    options,
    height,
    width,
  } = props;

  const [codeEditorObj, setCodeEditorObj] = useState(
    getInitialCodeEditorObj({ input, inputFormat })
  );

  const { t } = useI18n(["common"]);

  const dispatch = useDispatch();

  const selectedLanguageLabel = _.get(
    _.find(CODE_EDITOR_PROGRAMMING_LANGUAGES, { key: codeEditorObj.language }),
    "label"
  );

  const handleEditorDidMount = monaco => {
    monaco.editor.defineTheme("toddleTheme", themeJson);
  };

  const handleCodeChange = updatedCode => {
    setCodeEditorObj({
      ...codeEditorObj,
      code: updatedCode,
    });
  };

  const onDropdownOptionClick = ({ key }) => {
    setCodeEditorObj({
      language: key,
      code: "",
    });

    // to persist user language preference
    dispatch(
      updateSettings({
        settings: [
          {
            name: persistLanguagePreferenceSetting,
            value: JSON.stringify(key),
          },
        ],
        showToastMsg: false,
      })
    );
  };

  const onAddClick = () => {
    const output = getFormattedCode({
      codeEditorObj,
      outputFormat,
    });
    onAddCodeHandler(output);
    onCloseHandler();
  };

  const dropdownOverlay = () => {
    return (
      <DropdownMenu
        dsVersion="2.0"
        options={CODE_EDITOR_PROGRAMMING_LANGUAGES}
        showSelection={true}
        value={codeEditorObj.language}
        onClick={onDropdownOptionClick}
      />
    );
  };

  return (
    <Modal
      dsVersion="2.0"
      isOpen={isOpen}
      onClose={onCloseHandler}
      height={height}
      width={width}
    >
      <Modal.Header isClosable={false}>
        <div className={classes.headerContainer}>
          <h6>{t("ai:code_editor")}</h6>
          <div className={classes.headerRightContainer}>
            <Dropdown overlay={dropdownOverlay()}>
              <ButtonDisclosure
                variant="neutral"
                type="plain"
                dsVersion="2.0"
                size="medium"
              >
                {selectedLanguageLabel}
              </ButtonDisclosure>
            </Dropdown>
            <IconButton
              dsVersion="2.0"
              icon={<CloseOutlined />}
              size="medium"
              variant="neutral"
              type="plain"
              onClick={onCloseHandler}
            />
          </div>
        </div>
      </Modal.Header>
      <Modal.Body style={styles.modalBodyStyle}>
        <div className={classes.bodyContainer}>
          <Editor
            value={codeEditorObj.code}
            language={codeEditorObj.language}
            beforeMount={handleEditorDidMount}
            theme="toddleTheme"
            className={classes.editorContainer}
            loading={<Loading containerStyle={{ height: "100%" }} />}
            onChange={handleCodeChange}
            options={options}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.footerContainer}>
          <Button
            variant="neutral"
            type="outlined"
            dsVersion="2.0"
            size="medium"
            onClick={onCloseHandler}
          >
            {t("common:cancel")}
          </Button>
          <Button
            variant="destructive"
            dsVersion="2.0"
            size="medium"
            onClick={onAddClick}
          >
            {t("common:add")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

CodeEditor.defaultProps = {
  isOpen: true,
  onCloseHandler: () => {},
  onAddCodeHandler: () => {},
  inputFormat: MARKDOWN,
  outputFormat: MARKDOWN,
  input: "",
  options: {
    minimap: {
      enabled: false,
    },
    fontSize: 16,
  },
};

CodeEditor.propTypes = {
  isOpen: PropTypes.bool,
  onCloseHandler: PropTypes.func,
  onAddCodeHandler: PropTypes.func,
  inputFormat: PropTypes.string,
  outputFormat: PropTypes.string,
  input: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  options: PropTypes.object,
};

export default CodeEditor;
